// Run detail for sphinx runs: a case × target matrix fed by GET /runs/:id
// (polled while live) plus the SSE event stream, a cancel button, and a
// per-iteration drawer (transcript, matcher result, judge verdict).
import { useMemo, useState } from "react";
import { ArrowLeft, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@sphinx/design-system/badge";
import { Button } from "@sphinx/design-system/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@sphinx/design-system/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sphinx/design-system/tooltip";
import { cn } from "@/lib/utils";
import { cancelSphinxRun, useSphinxRun } from "@/data/evals";
import { targetDisplayName } from "@/data/targets";
import type { SphinxIteration } from "@/lib/sphinx-api";
import type {
  ClientTranscript,
  Grade,
  ObservedToolCall,
  TargetId,
} from "@/shared/sphinx";
import {
  EVAL_FAILED_BADGE_STRONG_CLASS,
  EVAL_PASSED_BADGE_STRONG_CLASS,
} from "./constants";
import { formatRunId } from "./helpers";
import {
  clientModelKey,
  isTerminalStatus,
  matrixCellStatus,
  mergeIteration,
  summarizeCells,
  UNGRADED_CELL_EXPLANATION,
  type MatrixCellKind,
  type MatrixCellStatus,
} from "./sphinx-run-matrix";
import type { LiveIteration } from "./sphinx-run-stream-reducer";
import { useSphinxRunStream } from "./use-sphinx-run-stream";
import type { EvalCase } from "./types";

interface SphinxRunDetailProps {
  runId: string;
  cases: EvalCase[];
  onBack: () => void;
}

export const CELL_CLASS: Record<MatrixCellKind, string> = {
  pending: "bg-muted text-muted-foreground",
  running: "bg-warning/20 text-foreground",
  passed: EVAL_PASSED_BADGE_STRONG_CLASS,
  failed: EVAL_FAILED_BADGE_STRONG_CLASS,
  error: EVAL_FAILED_BADGE_STRONG_CLASS,
  canceled: "bg-muted text-muted-foreground line-through",
  // Neutral, and deliberately not green: the iteration ran, the case asserted
  // nothing, so there is no verdict to report.
  ungraded: "bg-muted/70 text-foreground ring-1 ring-inset ring-border",
};

const RUN_STATUS_CLASS: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  running: "bg-warning/20 text-foreground",
  complete: EVAL_PASSED_BADGE_STRONG_CLASS,
  failed: EVAL_FAILED_BADGE_STRONG_CLASS,
  canceled: "bg-muted text-muted-foreground",
};

export function SphinxRunDetail({ runId, cases, onBack }: SphinxRunDetailProps) {
  const run = useSphinxRun(runId);
  const runTerminal = run ? isTerminalStatus(run.status) : false;
  const stream = useSphinxRunStream(runId, Boolean(run) && !runTerminal);
  const [openIterationId, setOpenIterationId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const iterations = useMemo(() => run?.iterations ?? [], [run]);

  const byCell = useMemo(() => {
    const map = new Map<string, SphinxIteration>();
    for (const it of iterations)
      map.set(`${it.caseId}::${clientModelKey(it.target, it.model)}`, it);
    return map;
  }, [iterations]);

  // Rows: the suite's cases in order, plus any iteration whose case has since
  // been deleted (shown under its frozen spec title so the run stays readable
  // — a suite re-seed replaces every case row under a new id, which left run
  // 5f914c14 as 29 blank title rows plus 29 "Case <id>" result rows). Suite
  // cases with no iterations in a populated run are those re-seeded strangers,
  // not pending work (every iteration row exists from enqueue), so they drop.
  const rows = useMemo(() => {
    const known = new Set(cases.map((c) => c._id));
    const used = new Set(iterations.map((it) => it.caseId));
    const frozenTitle = new Map(
      iterations.filter((it) => it.caseTitle).map((it) => [it.caseId, it.caseTitle as string])
    );
    const extra = [...used]
      .filter((id) => !known.has(id))
      .map((id) => ({ id, title: frozenTitle.get(id) ?? `Case ${formatRunId(id)}` }));
    return [
      ...cases
        .filter((c) => iterations.length === 0 || used.has(c._id))
        .map((c) => ({ id: c._id, title: c.title || "Untitled case" })),
      ...extra,
    ];
  }, [cases, iterations]);

  // Columns are (client, MODEL) pairs, not clients: a client with two models
  // enabled runs both (`iterations.model`, migration 012), and keying a column
  // by client alone would drop one of them on top of the other. Derived from
  // the iterations that exist, ordered by the run's client order; a run with no
  // iterations yet still shows its clients.
  const columns = useMemo(() => {
    const seen = new Map<
      string,
      { key: string; target: TargetId; model: string | null }
    >();
    for (const it of iterations) {
      const key = clientModelKey(it.target, it.model);
      if (!seen.has(key))
        seen.set(key, {
          key,
          target: it.target as TargetId,
          model: it.model ?? null,
        });
    }
    const order = new Map(
      (run?.targets ?? []).map((t, index) => [t as string, index])
    );
    if (seen.size === 0) {
      return (run?.targets ?? []).map((t) => ({
        key: clientModelKey(t, null),
        target: t,
        model: null as string | null,
      }));
    }

    return [...seen.values()].sort(
      (a, b) =>
        (order.get(a.target) ?? Number.MAX_SAFE_INTEGER) -
          (order.get(b.target) ?? Number.MAX_SAFE_INTEGER) ||
        (a.model ?? "").localeCompare(b.model ?? "")
    );
  }, [iterations, run?.targets]);

  const cellStatuses = useMemo(() => {
    const out = new Map<string, MatrixCellStatus>();
    for (const it of iterations)
      out.set(it.id, matrixCellStatus(it, stream.iterations[it.id]));
    return out;
  }, [iterations, stream.iterations]);

  const summary = useMemo(
    () => summarizeCells([...cellStatuses.values()]),
    [cellStatuses]
  );

  const openIteration = openIterationId
    ? iterations.find((it) => it.id === openIterationId) ?? null
    : null;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelSphinxRun(runId);
      toast.success("Run canceled");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel run");
    } finally {
      setCancelling(false);
    }
  };

  if (run === undefined) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (run === null) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        Run not found.
        <Button variant="outline" size="sm" onClick={onBack}>
          Back to suite
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden px-6 pb-6 pt-4"
      data-testid="sphinx-run-detail"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" />
          Suite
        </Button>
        <h2 className="text-base font-semibold">Run {formatRunId(run.id)}</h2>
        <Badge
          variant="outline"
          className={cn("border-transparent", RUN_STATUS_CLASS[run.status])}
          data-testid="sphinx-run-status"
        >
          {run.status === "running" || run.status === "pending" ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          ) : null}
          {run.status}
        </Badge>
        <Badge variant="outline" data-testid="sphinx-run-connector" title="Yarn env · org">
          yarn: {run.yarnEnv} · {run.yarnOrg}
        </Badge>
        <span className="text-xs text-muted-foreground" data-testid="sphinx-run-summary">
          {summary.passed} passed · {summary.failed} failed
          {summary.ungraded > 0 ? (
            <>
              {" · "}
              <span title={UNGRADED_CELL_EXPLANATION} data-testid="sphinx-run-ungraded">
                {summary.ungraded} ran (nothing asserted)
              </span>
            </>
          ) : null}{" "}
          · {summary.total} total
        </span>
        {run.note ? (
          <span className="truncate text-xs text-muted-foreground" title={run.note}>
            {run.note}
          </span>
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          {stream.runError ? (
            <span className="text-xs text-destructive" title={stream.runError}>
              {stream.runError}
            </span>
          ) : null}
          {!runTerminal ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              className="gap-1.5"
              data-testid="sphinx-run-cancel"
            >
              {cancelling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <XCircle className="h-3.5 w-3.5" />
              )}
              Cancel
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-lg border bg-card">
        <table className="w-full border-collapse text-sm" data-testid="sphinx-run-matrix">
          <thead className="sticky top-0 z-10 bg-muted/60 backdrop-blur">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Case
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground"
                >
                  {targetDisplayName(column.target)}
                  {column.model ? (
                    <span className="block text-[10px] font-normal normal-case tracking-normal text-muted-foreground/80">
                      {column.model}
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  No cases in this run.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="max-w-[28rem] truncate px-3 py-2 font-medium" title={row.title}>
                  {row.title}
                </td>
                {columns.map((column) => {
                  const it = byCell.get(`${row.id}::${column.key}`);
                  const status = it ? cellStatuses.get(it.id) : undefined;
                  return (
                    <td key={column.key} className="px-3 py-2">
                      {it && status ? (
                        <MatrixCell
                          status={status}
                          onClick={() => setOpenIterationId(it.id)}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet
        open={openIteration !== null}
        onOpenChange={(open) => {
          if (!open) setOpenIterationId(null);
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {openIteration ? (
            <IterationDrawer
              iteration={openIteration}
              live={stream.iterations[openIteration.id]}
              caseTitle={rows.find((r) => r.id === openIteration.caseId)?.title ?? ""}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function MatrixCell({
  status,
  onClick,
}: {
  status: MatrixCellStatus;
  onClick: () => void;
}) {
  const chip = (
    <button
      type="button"
      onClick={onClick}
      data-testid={`sphinx-cell-${status.kind}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium transition hover:brightness-95",
        CELL_CLASS[status.kind]
      )}
    >
      {status.kind === "running" ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      ) : null}
      {status.label}
    </button>
  );
  // An `ungraded` cell has no error to show but very much needs explaining:
  // "ran" next to real passes is only honest if you can find out why.
  // `detail` carries the judge's reasons (or the matcher's misses) so the
  // verdict's WHY is one hover away instead of a drawer-click.
  const tip =
    [status.error, status.detail].filter(Boolean).join("\n") ||
    (status.kind === "ungraded" ? UNGRADED_CELL_EXPLANATION : null);
  if (!tip) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent className="max-w-sm whitespace-pre-wrap break-words">
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Tool names Yarn's own side recorded for this iteration (`ServerTruth`:
 * `observedToolNames` from the S3 mcp-logs archive, falling back to the
 * normalized `tools/call` events). The authoritative record — shown when the
 * client transcript names no tools, which is common for the vision-driven
 * desktop targets where observed calls are best-effort inference.
 */
export function serverRecordedToolNames(value: unknown): string[] {
  if (!value || typeof value !== "object") return [];
  const truth = value as {
    observedToolNames?: unknown;
    events?: unknown;
  };
  const fromNames = Array.isArray(truth.observedToolNames)
    ? truth.observedToolNames.filter((n): n is string => typeof n === "string")
    : [];
  const names =
    fromNames.length > 0
      ? fromNames
      : Array.isArray(truth.events)
        ? truth.events
            .filter(
              (e): e is { method?: unknown; toolName?: unknown } =>
                !!e && typeof e === "object"
            )
            .filter((e) => e.method === "tools/call" && typeof e.toolName === "string")
            .map((e) => e.toolName as string)
        : [];
  return [...new Set(names)];
}

/**
 * The window's tools/call exchanges as full observed calls (name, args, result
 * summary), when — and only when — the window is attributable: no ambiguous
 * overlap means every call in it is this iteration's. The desktop apps barely
 * render tool use, so for scraped transcripts these ARE the tool calls.
 */
export function serverRecordedToolCalls(value: unknown): ObservedToolCall[] {
  if (!value || typeof value !== "object") return [];
  const truth = value as { events?: unknown; ambiguousOverlaps?: unknown };
  if (Array.isArray(truth.ambiguousOverlaps) && truth.ambiguousOverlaps.length > 0) return [];
  if (!Array.isArray(truth.events)) return [];
  return truth.events
    .filter(
      (e): e is { method?: unknown; toolName?: unknown; raw?: unknown; resultSummary?: unknown; isError?: unknown } =>
        !!e && typeof e === "object"
    )
    .filter((e) => e.method === "tools/call" && typeof e.toolName === "string")
    .map((e) => ({
      name: e.toolName as string,
      args: (e.raw as { args?: unknown } | undefined)?.args,
      ...(typeof e.resultSummary === "string" ? { resultSummary: e.resultSummary } : {}),
      ...(typeof e.isError === "boolean" ? { isError: e.isError } : {}),
    }));
}

export function transcriptOf(value: unknown): ClientTranscript | null {
  if (!value || typeof value !== "object") return null;
  const t = value as Partial<ClientTranscript>;
  return Array.isArray(t.turns) ? (t as ClientTranscript) : null;
}

const stringify = (v: unknown) =>
  typeof v === "string" ? v : JSON.stringify(v, null, 2);

/** Observed tool calls (name, collapsed args, result summary) — shared by the
 *  iteration drawer here and the Playground's dispatch columns. */
export function ObservedToolCallList({ calls }: { calls: ObservedToolCall[] }) {
  return (
    <ul className="space-y-1">
      {calls.map((call, j) => (
        <li key={j} className="rounded bg-muted/50 p-1.5">
          <div className="flex items-center gap-2">
            <code className="text-xs font-semibold">{call.name}</code>
            {/* Which connector served it — same tool once per org otherwise reads as duplicates. */}
            {call.server ? (
              <span className="text-[10px] text-muted-foreground">
                {call.server}
              </span>
            ) : null}
            {call.isError ? (
              <Badge className={EVAL_FAILED_BADGE_STRONG_CLASS}>error</Badge>
            ) : null}
          </div>
          {call.args !== undefined ? (
            <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-muted-foreground">
              {stringify(call.args)}
            </pre>
          ) : null}
          {call.resultSummary ? (
            <p className="mt-1 whitespace-pre-wrap break-words text-[11px]">
              → {call.resultSummary}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function IterationDrawer({
  iteration,
  live,
  caseTitle,
}: {
  iteration: SphinxIteration;
  live?: LiveIteration;
  caseTitle: string;
}) {
  const { status, grade, error } = mergeIteration(iteration, live);
  // A failed-before-run grade carries `matcher: null` despite the type.
  const matcher = (grade?.matcher ?? null) as Grade["matcher"] | null;
  const cell = matrixCellStatus(iteration, live);
  const transcript = transcriptOf(iteration.clientTranscript);
  const liveTurn =
    !transcript && live && (live.text || live.toolCalls.length > 0)
      ? {
          prompt: "",
          responseText: live.text,
          observedToolCalls: live.toolCalls.map((c) => ({
            name: c.name,
            args: c.args,
            resultSummary: c.resultSummary,
            isError: c.isError,
          })),
        }
      : null;
  const turns = transcript?.turns ?? (liveTurn ? [liveTurn] : []);

  return (
    <div className="space-y-5 text-sm" data-testid="sphinx-iteration-drawer">
      <SheetHeader className="px-0">
        <SheetTitle className="flex flex-wrap items-center gap-2">
          <span className="truncate">{caseTitle}</span>
          <Badge variant="outline">{targetDisplayName(iteration.target)}</Badge>
          {/* The model this iteration ran — the same client can appear twice
              in a run at two models, so it is part of the identity here. */}
          {iteration.model ?? live?.model ? (
            <Badge variant="outline">{iteration.model ?? live?.model}</Badge>
          ) : null}
          {/* Case-pinned org — part of the iteration's identity when a case
              runs against several orgs; absent = the run's own org. */}
          {iteration.yarnOrg ? (
            <Badge variant="outline">org: {iteration.yarnOrg}</Badge>
          ) : null}
        </SheetTitle>
        <SheetDescription className="flex flex-wrap items-center gap-2">
          <span
            className={cn("rounded-md px-2 py-0.5 text-xs font-medium", CELL_CLASS[cell.kind])}
            title={
              cell.kind === "ungraded" ? UNGRADED_CELL_EXPLANATION : undefined
            }
          >
            {cell.label}
          </span>
          {cell.kind === "ungraded" ? (
            <span
              className="text-xs text-muted-foreground"
              data-testid="sphinx-iteration-ungraded"
            >
              {UNGRADED_CELL_EXPLANATION}
            </span>
          ) : null}
          <span className="text-xs">status: {status}</span>
          {transcript ? (
            <span className="text-xs">transcript: {transcript.exactness}</span>
          ) : null}
        </SheetDescription>
      </SheetHeader>

      {error ? (
        <Section title="Error">
          <pre className="whitespace-pre-wrap break-words rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            {error}
          </pre>
        </Section>
      ) : null}

      <Section title="Transcript">
        {turns.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {status === "pending" ? "Waiting to start." : "No transcript yet."}
          </p>
        ) : (
          <div className="space-y-3">
            {turns.map((turn, i) => (
              <div key={i} className="space-y-2 rounded-md border p-2">
                {turn.prompt ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Prompt
                    </p>
                    <p className="whitespace-pre-wrap">{turn.prompt}</p>
                  </div>
                ) : null}
                {turn.observedToolCalls.length > 0 ? (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      Tool calls
                    </p>
                    <ObservedToolCallList calls={turn.observedToolCalls} />
                  </div>
                ) : null}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Response
                  </p>
                  <p className="whitespace-pre-wrap">
                    {turn.responseText || (
                      <span className="text-muted-foreground">
                        {status === "running" ? "…" : "(empty)"}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
        {turns.every((turn) => turn.observedToolCalls.length === 0) &&
        serverRecordedToolNames(iteration.serverTruth).length > 0 ? (
          <div className="mt-3" data-testid="sphinx-server-recorded-tools">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Tools (server-recorded)
            </p>
            <p className="text-xs text-muted-foreground">
              The client transcript names no tools; these are the calls Yarn's
              own logs recorded in this iteration's window.
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {serverRecordedToolNames(iteration.serverTruth).map((name) => (
                <Badge key={name} variant="outline" className="font-mono text-[11px]">
                  {name}
                </Badge>
              ))}
            </div>
          </div>
        ) : null}
      </Section>

      <Section title="Matcher">
        {matcher ? (
          <div className="space-y-1 text-xs">
            <MatcherRow label="Result">
              <span className={matcher.passed ? "text-success" : "text-destructive"}>
                {matcher.passed ? "pass" : "fail"}
              </span>
            </MatcherRow>
            <MatcherRow label="Expected">{matcher.expected.join(", ") || "—"}</MatcherRow>
            <MatcherRow label="Observed">{matcher.observed.join(", ") || "—"}</MatcherRow>
            <MatcherRow label="Missing">{matcher.missing.join(", ") || "—"}</MatcherRow>
            <MatcherRow label="Unexpected">{matcher.unexpected.join(", ") || "—"}</MatcherRow>
            {matcher.argMismatches.length > 0 ? (
              <MatcherRow label="Arg mismatches">
                <ul className="list-disc pl-4">
                  {matcher.argMismatches.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </MatcherRow>
            ) : null}
            {matcher.outputChecks.length > 0 ? (
              <MatcherRow label="Output checks">
                <ul className="space-y-0.5">
                  {matcher.outputChecks.map((c, i) => (
                    <li key={i}>
                      <span className={c.passed ? "text-success" : "text-destructive"}>
                        {c.passed ? "pass" : "fail"}
                      </span>{" "}
                      {c.kind}: {c.detail}
                    </li>
                  ))}
                </ul>
              </MatcherRow>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {grade ? "No matcher result (the iteration did not run)." : "Not graded yet."}
          </p>
        )}
      </Section>

      <Section title="Judge">
        {grade?.judge ? (
          <div className="space-y-1 text-xs">
            <MatcherRow label="Verdict">
              <span
                className={
                  grade.judge.verdict === "pass"
                    ? "text-success"
                    : grade.judge.verdict === "fail"
                      ? "text-destructive"
                      : "text-muted-foreground"
                }
              >
                {grade.judge.verdict}
              </span>{" "}
              ({Math.round(grade.judge.score * 100)}%)
            </MatcherRow>
            {grade.judge.reasons.length > 0 ? (
              <MatcherRow label="Reasons">
                <ul className="list-disc pl-4">
                  {grade.judge.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </MatcherRow>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {grade ? "No judge verdict for this iteration." : "Not graded yet."}
          </p>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      {children}
    </section>
  );
}

function MatcherRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <span className="text-muted-foreground">{label}</span>
      <div className="break-words">{children}</div>
    </div>
  );
}
